import styled from 'styled-components';
import { Children, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useSwiper } from 'swiper/react';
import { device } from '../../utils/styled-component-mixins';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import ButtonLink from './ButtonLinkComponent';
import ConditionalWrapper from './ConditionalWrapper';

const HomeHeroMainSlideWrapper = styled.div`
  background-position: left center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  padding-block-start: 3.6rem;
  position: relative;
  ${(props) => props.mobileImage && `background-image: url('${props.mobileImage}');`}

  @media ${device.desktop} {
    background-image: none;
  }

  > * {
    padding-inline: 1.5rem;

    @media ${device.desktop} {
      padding-inline: 7rem 0;
    }
  }

  h1 {
    margin-bottom: 0.8rem;

    @media ${device.desktop} {
      margin-bottom: 1.6rem;
      margin-top: 8rem;
      max-width: 35%;
      font-size: 3.6rem;
      line-height: 4rem;
    }

    @media ${device.desktopXl} {
      max-width: 39%;
      font-size: 5rem;
      line-height: 5.6rem;
    }

    @media (min-width: 1500px) {
      max-width: 35%;
    }

    @media (min-width: 1600px) {
      max-width: 40%;
    }

    @media (min-width: 1700px) {
      max-width: 45%;
    }
  }

  p {
    font-size: 1.8rem;
    margin-top: 0;
    margin-bottom: 2.4rem;

    @media ${device.desktop} {
      font-size: 2.6rem;
      line-height: 4rem;
      margin-bottom: 4rem;
      max-width: 40%;
    }
  }

  .btn-wrapper:first-of-type {
    margin-bottom: 1.6rem;
  }

  div:has(.btn-wrapper) {
    height: unset;
  }
`;

const OnLoadAnimation = (props) => {
  const swiper = useSwiper();
  const shouldReduceMotion = useReducedMotion();

  /** @type {import("framer-motion").Variants} */
  const variants = {
    preload: (index) => ({
      opacity: shouldReduceMotion ? 0 : 1,
      y: shouldReduceMotion ? 0 : `calc(25px * ${index} + 100px)`,
      x: swiper.realIndex === props.slideIndex ? '100%' : 0,
      ...(shouldReduceMotion ? { opacity: swiper.realIndex === props.slideIndex ? 1 : 0 } : {}),
      transition: { duration: 0 }
    }),
    load: shouldReduceMotion ? {} : { y: 0 },
    slideTransitionStart: (index) =>
      swiper.previousIndex === props.slideIndex && {
        ...(shouldReduceMotion ? { opacity: 0 } : { x: 0 }),
        transition: {
          duration: shouldReduceMotion ? 0.3 : 0.5,
          delay: shouldReduceMotion ? 0 : index * 0.15,
          ease: shouldReduceMotion ? 'easeInOut' : [0.755, 0.05, 0.855, 0.06]
        },
        transitionEnd: shouldReduceMotion
          ? {
              x: 0
            }
          : null
      },
    slideTransitionEnd: (index) =>
      swiper.realIndex === props.slideIndex && {
        x: '100%',
        opacity: shouldReduceMotion ? 0 : 1,
        transition: {
          delay: shouldReduceMotion ? 0 : index * 0.15,
          duration: shouldReduceMotion ? 0 : 1,
          ease: shouldReduceMotion ? 'easeInOut' : [0.23, 1, 0.32, 1]
        }
      },

    slideTransitionAfter: () =>
      swiper.realIndex === props.slideIndex && {
        opacity: 1,
        transition: {
          ease: 'easeInOut'
        }
      }
  };

  return Children.map(props.children, (child, index) => (
    <motion.div
      custom={index}
      animate={props.controls}
      transition={{ duration: 1, ease: 'easeInOut' }}
      variants={variants}
    >
      {child}
    </motion.div>
  ));
};

const HomeHeroMainSlide = (props) => {
  const isDesktop = useMediaQuery(device.desktop, true);
  const { index, item, controls } = props;

  const load = async () => {
    await controls?.start('preload');
    await controls?.start('load');
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HomeHeroMainSlideWrapper mobileImage={item.mobileImage} media={item.media}>
      <ConditionalWrapper
        condition={isDesktop}
        wrapper={(children) => (
          <OnLoadAnimation controls={controls} slideIndex={index}>
            {children}
          </OnLoadAnimation>
        )}
      >
        <h1>{item.heading}</h1>
        <p>{item.subheading}</p>
        {item.ctaOneType && (
          <ButtonLink
            text={item.ctaOneText}
            link={item.ctaOneLink}
            type={item.ctaOneType}
            icon={item.ctaOneIcon}
            theme="light"
          />
        )}
        {item.ctaTwoType && (
          <ButtonLink
            text={item.ctaTwoText}
            link={item.ctaTwoLink}
            type={item.ctaTwoType}
            icon={item.ctaTwoIcon}
            theme="light"
          />
        )}
      </ConditionalWrapper>
    </HomeHeroMainSlideWrapper>
  );
};

export default HomeHeroMainSlide;
